import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, SlidersHorizontal, FilePlus, FileText, Download, ExternalLink, Search, History, ArrowLeft, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { listPreventivi, downloadPreventivoPdf, fmtEur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NuovoPreventivoDialog } from "@/components/NuovoPreventivoDialog";

const FASCIA = { listino: "Listino", scontato: "Scontato", ultima: "Ultima offerta" };

export default function AdminPreventivi() {
  const { admin, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [dl, setDl] = useState(null);

  useEffect(() => {
    listPreventivi().then((d) => setRows(d.preventivi || [])).catch(() => toast.error("Errore nel caricamento dei preventivi")).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [r.ragione_sociale, r.richiesto_da, r.numero].some((v) => (v || "").toLowerCase().includes(s)));
  }, [rows, q]);

  const download = async (r) => {
    setDl(r.lead_id);
    try {
      await downloadPreventivoPdf(r.lead_id, `preventivo_${(r.numero || "").replace(/\//g, "-")}.pdf`);
    } catch {
      toast.error("Errore nel download del PDF");
    } finally {
      setDl(null);
    }
  };

  const totImp = filtered.reduce((a, r) => a + (r.imponibile || 0), 0);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 glass">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-400 hidden sm:block">{admin?.email}</span>
            <Link to="/admin" data-testid="prev-back-leads" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-gold-400 transition-colors"><ArrowLeft className="h-4 w-4" /> Lead & Audit</Link>
            <Link to="/admin/settings" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-gold-400 transition-colors"><SlidersHorizontal className="h-4 w-4" /> Offerte & Listino</Link>
            <button onClick={logout} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-400 transition-colors"><LogOut className="h-4 w-4" /> Esci</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8" data-testid="preventivi-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading font-black text-3xl text-white tracking-tight flex items-center gap-2"><FileText className="h-7 w-7 text-gold-400" /> Preventivi</h1>
            <p className="text-slate-400 mt-1">Archivio di tutti i preventivi fotovoltaici salvati: scarica il PDF, riapri per modificare o creane uno nuovo.</p>
          </div>
          <Button onClick={() => setShowNew(true)} data-testid="preventivi-new-btn" className="h-10 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold">
            <FilePlus className="h-4 w-4 mr-1.5" /> Nuovo preventivo
          </Button>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[16rem] max-w-md">
            <Search className="h-4 w-4 text-slate-500 absolute left-3 top-3" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per cliente, richiesto da / rivenditore o numero…" className="input-dark h-10 pl-9" data-testid="preventivi-search" />
          </div>
          <span className="text-xs text-slate-400" data-testid="preventivi-count">{filtered.length} preventivi · imponibile totale <b className="text-slate-200 font-mono-plex">{fmtEur(totImp)}</b></span>
        </div>

        <div className="mt-4 card-dark rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-400" data-testid="preventivi-empty">Nessun preventivo salvato. Clicca "Nuovo preventivo" per crearne uno.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/10">
                  <th className="px-4 py-3">Numero</th><th className="px-4 py-3">Data</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Richiesto da</th>
                  <th className="px-4 py-3 text-right">kWp</th><th className="px-4 py-3">Fascia</th><th className="px-4 py-3 text-right">Imponibile</th><th className="px-4 py-3 text-right">Totale</th><th className="px-4 py-3 text-center">Vers.</th><th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.lead_id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`preventivo-row-${r.lead_id}`}>
                    <td className="px-4 py-3 font-mono-plex text-white">{r.numero}</td>
                    <td className="px-4 py-3 text-slate-300">{r.data}</td>
                    <td className="px-4 py-3 text-slate-100 font-medium">{r.ragione_sociale}{r.manual && <span className="ml-2 text-[10px] uppercase tracking-wider text-sky-300 bg-sky-500/10 px-1.5 py-0.5 rounded-full">manuale</span>}</td>
                    <td className="px-4 py-3 text-slate-400">{r.richiesto_da || "—"}</td>
                    <td className="px-4 py-3 text-right text-slate-200 font-mono-plex">{r.potenza_kwp ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-300">{FASCIA[r.fascia_listino] || r.fascia_listino}</td>
                    <td className="px-4 py-3 text-right text-slate-200 font-mono-plex">{fmtEur(r.imponibile)}</td>
                    <td className="px-4 py-3 text-right text-gold-300 font-mono-plex font-semibold">{fmtEur(r.totale)}</td>
                    <td className="px-4 py-3 text-center text-slate-400">{r.versioni > 0 ? <span className="inline-flex items-center gap-1"><History className="h-3 w-3" /> {r.versioni}</span> : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <button type="button" onClick={() => download(r)} disabled={dl === r.lead_id} className="inline-flex items-center gap-1 rounded-md bg-gold-500/15 border border-gold-500/30 px-2.5 py-1.5 text-xs text-gold-300 hover:bg-gold-500/25" data-testid={`preventivo-pdf-${r.lead_id}`}>
                          {dl === r.lead_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                        </button>
                        <Link to={`/admin/leads/${r.lead_id}?preventivo=1`} className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2.5 py-1.5 text-xs text-slate-200 hover:bg-white/5" data-testid={`preventivo-open-${r.lead_id}`}>
                          <ExternalLink className="h-3.5 w-3.5" /> Apri / Modifica
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      <NuovoPreventivoDialog open={showNew} onOpenChange={setShowNew} />
    </div>
  );
}
